import { FocusTrap } from "@iunctim/component-overlay/Resources/Public/Scripts/features/FocusTrap.js";

/**
 * Class to implement the dropdown functionality
 * for a single instance; the application is done
 * via the DropdownManager class
 */
export class Dropdown {
    private focusTrap: FocusTrap;

    /**
     * Constructor of the Dropdown
     *
     * @param {HTMLElement} menu
     * @param {HTMLElement} toggler
     */
    public constructor(
        private menu: HTMLElement,
        private toggler: HTMLElement,
    ) {
        this.focusTrap = new FocusTrap(this.menu);

        this.toggler.addEventListener("click", this.onToggle);
        this.menu.addEventListener("keydown", this.onKeydown);
        this.menu.addEventListener("click", this.onMenuClick);
        document.addEventListener("click", this.onDocumentClick);
    }

    /**
     * Method to open the dropdown
     *
     * @param {boolean} [useFocusTrap=false]
     * @returns {void}
     */
    public open(useFocusTrap: boolean = false): void {
        // Get the focus-trap and enable it
        if (useFocusTrap) {
            this.focusTrap.enable(this.toggler);
        } else {
            this.focusTrap.setInitial(this.toggler);
        }

        // Set aria-expanded to true on the toggler
        this.toggler?.setAttribute("aria-expanded", "true");

        // Position the menu in case the viewport got rescaled;
        // please note that this has to be done when the menu is
        // visible, otherwise the menu height returns 0 - therefore
        // we call this method at the very end
        this.positionMenu();
    }

    /**
     * Method to close the dropdown
     *
     * @param {boolean} [refocus=false]
     * @returns {void}
     */
    public close(refocus: boolean = false): void {
        // Get the focus-trap and disable it
        this.focusTrap.disable(refocus);

        // Set aria-expanded to false on the toggler
        this.toggler?.setAttribute("aria-expanded", "false");
    }

    /**
     * Method to toggle the dropdown
     *
     * @param {boolean} [byKeyboard=false]
     * @returns {void}
     */
    public toggle(byKeyboard: boolean = false): void {
        if (this.active) {
            return this.close(byKeyboard);
        }
        this.open(byKeyboard);
    }

    public destroy(): void {
        this.close();
        this.toggler.removeEventListener("click", this.onToggle);
        this.menu.removeEventListener("keydown", this.onKeydown);
        this.menu.removeEventListener("click", this.onMenuClick);
        document.removeEventListener("click", this.onDocumentClick);
    }

    private onToggle = (event: MouseEvent): void => {
        this.toggle(event.detail === 0);
    };

    private onKeydown = (event: KeyboardEvent): void => {
        if (this.active && event.key === "Escape") {
            event.preventDefault();
            this.close(true);
        }
    };

    private onMenuClick = (event: MouseEvent): void => {
        this.close(event.detail === 0);
    };

    private onDocumentClick = (event: MouseEvent): void => {
        const target = event.target as Node | null;
        if (this.active && !this.toggler.contains(target)) {
            this.close(event.detail === 0);
        }
    };

    /**
     * Method to position the menu relative to the toggler
     * depending on the position of the toggler in the viewport,
     * given that either position or alignment are set to "auto"
     *
     * @returns {void}
     */
    private positionMenu(): void {
        const rect = this.toggler.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        // Determine the position
        const menuHeight = this.menu?.offsetHeight ?? 1;
        const togglerHeight = this.toggler?.offsetHeight ?? 0;
        const offset = 5;

        let top = 0;
        if (centerY > window.innerHeight / 2) {
            top -= menuHeight + offset;
        } else {
            top += togglerHeight + offset;
        }
        this.menu.style.top = `${top / 16}rem`;

        // Determine the alignment
        if (centerX < window.innerWidth / 2) {
            this.menu.setAttribute("alignment", "left");
        } else {
            this.menu.setAttribute("alignment", "right");
        }
    }

    /**
     * Helper method to determine whether
     * the dropdown is open or closed
     *
     * @returns {boolean}
     */
    private get active(): boolean {
        return this.toggler?.getAttribute("aria-expanded") === "true";
    }
}
